/**
 * GameState.ts
 * Central state singleton and coordinator for American Dream: Pocket Life.
 * Links SurvivalEngine simulation with StorageService persistence.
 */

import { FullGameState, SurvivalStats, ActiveBuff, PlayerCareerState } from './Types';
import { EventBus } from './EventBus';
import { SurvivalEngine } from './SurvivalEngine';
import { StorageService } from '../storage/StorageService';
import { backfillNonFlagshipSouvenirsFromVisits } from '../content/StateSouvenirsRegistry';

export class GameStateManager {
  private static instance: GameStateManager;
  private state!: FullGameState;
  private survivalEngine!: SurvivalEngine;
  private storageService!: StorageService;
  private saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubscribes: Array<() => void> = [];

  private constructor() {}

  public static getInstance(): GameStateManager {
    if (!GameStateManager.instance) {
      GameStateManager.instance = new GameStateManager();
    }
    return GameStateManager.instance;
  }

  public init(initialState: FullGameState, storage: StorageService): void {
    if (this.saveDebounceTimer !== null) {
      clearTimeout(this.saveDebounceTimer);
      this.saveDebounceTimer = null;
    }
    if (this.survivalEngine) {
      this.survivalEngine.stop();
    }
    for (const unsub of this.unsubscribes) {
      unsub();
    }
    this.unsubscribes = [];

    this.state = initialState;
    backfillNonFlagshipSouvenirsFromVisits(this.state);
    this.storageService = storage;

    // Register state getter with StorageService for periodic saves
    this.storageService.registerStateGetter(() => this.state);

    // Initialize and start SurvivalEngine
    this.survivalEngine = new SurvivalEngine(this.state.stats, this.state.activeBuffs);
    this.survivalEngine.start();

    // Listen to survival updates
    this.unsubscribes.push(
      EventBus.on('STATS_UPDATED', (payload: { stats?: SurvivalStats } & Partial<SurvivalStats>) => {
        const nextStats = payload?.stats ?? (payload as SurvivalStats);
        if (nextStats && typeof nextStats.energy === 'number') {
          Object.assign(this.state.stats, nextStats);
        }
        this.state.lastActiveTimestamp = Date.now();
        this.triggerDebouncedSave();
      }),
      EventBus.on('BUFFS_UPDATED', (buffs: ActiveBuff[]) => {
        if (Array.isArray(buffs)) {
          this.state.activeBuffs = [...buffs];
        }
        this.triggerDebouncedSave();
      })
    );
  }

  public getSurvivalEngine(): SurvivalEngine {
    return this.survivalEngine;
  }

  public getState(): FullGameState {
    return this.state;
  }

  public modifyMoney(delta: number): boolean {
    if (this.state.stats.money + delta < 0) {
      return false; // Insufficient funds
    }
    this.survivalEngine.applyStatDelta({ money: delta });
    return true;
  }

  public setCareer(career: Partial<PlayerCareerState>): void {
    this.state.career = { ...this.state.career, ...career };
    if (this.state.career.currentTitle) {
      EventBus.emit('CAREER_UPDATED', { title: this.state.career.currentTitle });
    }
    this.triggerDebouncedSave();
  }

  public triggerDebouncedSave(): void {
    if (this.saveDebounceTimer !== null) {
      clearTimeout(this.saveDebounceTimer);
    }
    this.saveDebounceTimer = setTimeout(() => {
      this.saveSync();
    }, 1000);
  }

  public async saveSync(): Promise<void> {
    if (this.saveDebounceTimer !== null) {
      clearTimeout(this.saveDebounceTimer);
      this.saveDebounceTimer = null;
    }
    this.state.lastActiveTimestamp = Date.now();
    await this.storageService.saveGameState(this.state);
  }
}

export const GameState = GameStateManager.getInstance();
